import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  ChevronRight,
  Search,
  Users,
} from 'lucide-react'
import { ActionButton, Badge, Button, Card } from '@/components/ui'
import { PartnerPriorityReviewRequestCard } from '@/components/pedagogy/PartnerPriorityReviewRequestCard'
import { useStudioStore } from '@/store'
import { PartnerTeacherAnalyticsTable } from '@/components/pedagogy/PartnerTeacherAnalyticsTable'
import { isTeacherAppEnabledForPartner } from '@/lib/teacherAppFeature'
import { PartnerTeacherAnalyticsDetailPage } from '@/pages/PartnerTeacherAnalyticsDetailPage'
import {
  getPartnerCityTeachers,
  type PartnerTeacherDashboard,
  type PartnerTeacherListItem,
  type PartnerTeacherStatus,
} from '@/services/partnerTeachersService'

type TeacherFilter = 'all' | 'working' | 'training' | 'candidate' | 'dismissed' | 'attention'

const statusLabels: Record<PartnerTeacherStatus, string> = {
  candidate: 'Кандидат',
  training: 'Обучение',
  working: 'Работает',
  dismissed: 'Уволен',
}

const qualificationLabels: Record<string, string> = {
  stretch_pilates: 'Растяжка и пилатес',
  universal: 'Универсал',
}

function formatDate(value: string | null): string {
  if (!value) return '—'
  const normalized = value.includes('T') ? value : `${value}T00:00:00`
  const date = new Date(normalized)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' }).format(date)
}


function formatPercent(value: number | null): string {
  if (value == null) return '—'
  return `${Math.round(value)}%`
}

function formatReviewSourceLine(label: string, date: string | null, quality: number | null): string {
  if (!date && quality == null) return `${label} —`
  const parts = [label]
  if (date) parts.push(formatDate(date))
  if (quality != null) parts.push(formatPercent(quality))
  return parts.join(' · ')
}

function statusVariant(status: PartnerTeacherStatus): 'success' | 'warning' | 'neutral' {
  if (status === 'working') return 'success'
  if (status === 'candidate' || status === 'training') return 'warning'
  return 'neutral'
}

function teacherInitials(fullName: string): string {
  return fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('') || 'П'
}

function trainingStatusLine(teacher: PartnerTeacherListItem): string {
  if (teacher.status === 'dismissed') return 'Больше не работает у этого франчайзи'

  const training = teacher.currentTraining
  if (training) {
    if (training.firstAttestationResult !== 'passed') {
      return training.firstAttestationDate
        ? `1-я аттестация · ${formatDate(training.firstAttestationDate)}`
        : 'Ожидается 1-я аттестация'
    }
    if (training.secondAttestationResult !== 'passed') {
      return training.secondAttestationDate
        ? `2-я аттестация · ${formatDate(training.secondAttestationDate)}`
        : 'Ожидается 2-я аттестация'
    }
    if (training.thirdAttestationResult !== 'passed') {
      return training.thirdAttestationDate
        ? `3-я аттестация · ${formatDate(training.thirdAttestationDate)}`
        : 'Ожидается 3-я аттестация'
    }
    return 'Обучение завершается'
  }

  if (teacher.status === 'working') return 'Обучение успешно завершено'
  return 'Статус обучения уточняется'
}

function teacherNowLine(teacher: PartnerTeacherListItem): string {
  if (teacher.status === 'dismissed') return 'Больше не работает у этого франчайзи'

  // «Сейчас» показывает только самый важный актуальный сигнал.
  if (teacher.latestReviewQualityPercent != null && teacher.latestReviewQualityPercent < 50) {
    return `Последний отсмотр ${formatPercent(teacher.latestReviewQualityPercent)}`
  }

  if (teacher.currentTraining) return trainingStatusLine(teacher)

  if (teacher.latestReviewDate) {
    return `Последний отсмотр ${formatPercent(teacher.latestReviewQualityPercent)}`
  }

  return '—'
}

function canViewTeachers(accessKind: string | undefined): boolean {
  return accessKind === 'owner' || accessKind === 'manager' || accessKind === 'management_company' || accessKind === 'senior_teacher'
}

export function PartnerTeachersPage() {
  const { partnerNumber = '', studioId = '' } = useParams<{ partnerNumber: string; studioId: string }>()
  const navigate = useNavigate()
  const [viewParams, setViewParams] = useSearchParams()
  const { studios, studiosSynced } = useStudioStore()
  const parsedStudioId = Number(studioId)
  const studio = studios.find(item => item.studio_internal_id === parsedStudioId)
  const hasFullPedagogyAnalyticsAccess = studio?.role === 'owner'
    || studio?.role === 'manager'
    || studio?.role === 'senior_teacher'
    || studio?.role === 'management_company'
    || studio?.access_kind === 'owner'
    || studio?.access_kind === 'senior_teacher'
    || studio?.access_kind === 'management_company'
  const teacherAccessEnabled = isTeacherAppEnabledForPartner(Number(partnerNumber))
  const analyticsEnabled = teacherAccessEnabled || hasFullPedagogyAnalyticsAccess
  // Senior access is an overlay on the ordinary studio row: the role is
  // authoritative even when access_kind is still "staff".
  const isSeniorTeacher = studio?.role === 'senior_teacher' || studio?.access_kind === 'senior_teacher'
  const isOrdinaryTeacher = analyticsEnabled && studio?.role === 'teacher' && !isSeniorTeacher
  const isAnalyticsView = analyticsEnabled && (viewParams.get('view') === 'analytics' || isOrdinaryTeacher)
  const hasAccess = studio ? canViewTeachers(studio.access_kind) || studio.role === 'manager' || isOrdinaryTeacher || isSeniorTeacher : false
  const analyticsCityId = Number(viewParams.get('city')) || studio?.city_id || null

  useEffect(() => {
    if (!analyticsCityId || viewParams.get('city')) return
    const next = new URLSearchParams(viewParams)
    next.set('city', String(analyticsCityId))
    setViewParams(next, { replace: true })
  }, [analyticsCityId, setViewParams, viewParams])

  const [dashboard, setDashboard] = useState<PartnerTeacherDashboard | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<TeacherFilter>('working')
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null)
  const [isWide, setIsWide] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1100px)').matches)

  useEffect(() => {
    if (typeof window === 'undefined') return
    const media = window.matchMedia('(min-width: 1100px)')
    const onChange = () => setIsWide(media.matches)
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (!Number.isInteger(parsedStudioId) || parsedStudioId <= 0 || !hasAccess || isOrdinaryTeacher) {
      setLoading(false)
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    // Load all statuses once; the chips below filter the complete partner list
    // locally and keep their counters stable.
    getPartnerCityTeachers(parsedStudioId, '', true)
      .then(data => {
        if (cancelled) return
        setDashboard(data)
        setSelectedTeacherId(current => (
          current && data.items.some(item => item.teacherId === current)
            ? current
            : data.items[0]?.teacherId ?? null
        ))
      })
      .catch(loadError => {
        if (cancelled) return
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить педагогов')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [hasAccess, isOrdinaryTeacher, parsedStudioId])

  const statusCounts = useMemo(() => {
    const items = dashboard?.items ?? []
    return {
      all: items.length,
      working: items.filter(item => item.status === 'working').length,
      training: items.filter(item => item.status === 'training').length,
      candidate: items.filter(item => item.status === 'candidate').length,
      dismissed: items.filter(item => item.status === 'dismissed').length,
      attention: items.filter(item => item.status !== 'dismissed' && item.attentionItems.length > 0).length,
    }
  }, [dashboard?.items])

  const filteredTeachers = useMemo(() => {
    const query = search.trim().toLocaleLowerCase('ru-RU')
    return (dashboard?.items ?? []).filter(teacher => {
      if (filter === 'working' && teacher.status !== 'working') return false
      if (filter === 'training' && teacher.status !== 'training') return false
      if (filter === 'candidate' && teacher.status !== 'candidate') return false
      if (filter === 'dismissed' && teacher.status !== 'dismissed') return false
      if (filter === 'attention' && (teacher.status === 'dismissed' || teacher.attentionItems.length === 0)) return false
      if (!query) return true
      const qualification = teacher.qualification ? qualificationLabels[teacher.qualification] : ''
      return teacher.fullName.toLocaleLowerCase('ru-RU').includes(query)
        || qualification.toLocaleLowerCase('ru-RU').includes(query)
    })
  }, [dashboard?.items, filter, search])

  const selectedTeacher = useMemo(() => (
    (dashboard?.items ?? []).find(item => item.teacherId === selectedTeacherId) ?? null
  ), [dashboard?.items, selectedTeacherId])

  const openTeacher = (teacher: PartnerTeacherListItem) => {
    if (isWide) {
      setSelectedTeacherId(teacher.teacherId)
      return
    }
    navigate(`/partners/${partnerNumber}/studios/${studioId}/teachers/${teacher.teacherId}`)
  }

  if (studiosSynced && (!studio || !hasAccess)) {
    return <Navigate to={`/partners/${partnerNumber}/studios/${studioId}/analytics`} replace />
  }

  if (isAnalyticsView) {
    if (isOrdinaryTeacher) return <PartnerTeacherAnalyticsDetailPage />
    return (
      <div className="space-y-4 pb-24">
        <div className="space-y-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Педагоги</h1>
          </div>
          {!isOrdinaryTeacher && analyticsEnabled && <nav className="flex gap-6 border-b border-border" aria-label="Разделы педагогов"><button type="button" className="inline-flex h-10 items-center border-b-2 border-transparent px-1 text-sm font-medium text-muted-foreground hover:text-foreground" onClick={() => { const next = new URLSearchParams(viewParams); next.delete('view'); setViewParams(next, { replace: true }) }}>Реестр педагогов</button><button type="button" className="inline-flex h-10 items-center border-b-2 border-primary px-1 text-sm font-medium text-foreground">Аналитика</button></nav>}
        </div>
        {analyticsCityId ? <PartnerTeacherAnalyticsTable partnerNumber={partnerNumber} studioId={parsedStudioId} teacherId={isOrdinaryTeacher ? undefined : null} seniorTeacherIds={new Set((dashboard?.items ?? []).filter(item => item.isSeniorTeacher).map(item => item.teacherId))} onOpenTeacher={teacherId => navigate(`/partners/${partnerNumber}/studios/${studioId}/teachers/analytics/${teacherId}`)} /> : <Card>Город не определён.</Card>}
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-24">
      <div className="space-y-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Педагоги</h1>
        </div>

        {analyticsEnabled && <nav className="flex gap-6 border-b border-border" aria-label="Разделы педагогов"><button type="button" className="inline-flex h-10 items-center border-b-2 border-primary px-1 text-sm font-medium text-foreground">Реестр педагогов</button><button type="button" className="inline-flex h-10 items-center border-b-2 border-transparent px-1 text-sm font-medium text-muted-foreground hover:text-foreground" onClick={() => { const next = new URLSearchParams(viewParams); next.set('view', 'analytics'); setViewParams(next, { replace: true }) }}>Аналитика</button></nav>}
      </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {teacherAccessEnabled && studio?.role === 'owner' && Number.isInteger(parsedStudioId) && parsedStudioId > 0 && (
            <ActionButton
              className="h-10 rounded-lg px-4 text-sm"
              icon={<Users className="h-4 w-4" />}
              onClick={() => navigate(`/partners/${partnerNumber}/management?section=teacher-access#teacher-access-management`)}
            >
              Управление доступами
            </ActionButton>
          )}
          {hasAccess && (studio?.access_kind === 'senior_teacher' || studio?.role === 'senior_teacher') && Number.isInteger(parsedStudioId) && parsedStudioId > 0 && (
            <ActionButton
              tone="primary"
              className="h-10 rounded-lg px-4 text-sm"
              icon={<ClipboardCheck className="h-4 w-4" />}
              onClick={() => navigate(`/partners/${partnerNumber}/studios/${studioId}/teachers/reviews/new`)}
            >
              Новый отсмотр
            </ActionButton>
          )}
          {hasAccess && (studio?.access_kind === 'owner' || studio?.role === 'manager' || studio?.access_kind === 'senior_teacher' || studio?.role === 'senior_teacher') && Number.isInteger(parsedStudioId) && parsedStudioId > 0 && (
            <PartnerPriorityReviewRequestCard
              studioInternalId={parsedStudioId}
              partnerNumber={partnerNumber}
              presentation="toolbar"
            />
          )}
        </div>
      {loading && <Card className="border border-border text-sm text-muted-foreground">Загружаем педагогов…</Card>}
      {error && <Card className="border border-error/30 bg-error/5 text-sm text-error">{error}</Card>}

      {!loading && !error && dashboard && (
        <>
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide lg:pb-0">
              <FilterChip
                active={filter === 'all'}
                onClick={() => setFilter('all')}
                label="Все"
                count={statusCounts.all}
                tone="neutral"
              />
              <FilterChip
                active={filter === 'working'}
                onClick={() => setFilter('working')}
                label="Работают"
                count={statusCounts.working}
                tone="success"
              />
              <FilterChip
                active={filter === 'training'}
                onClick={() => setFilter('training')}
                label="Обучение"
                count={statusCounts.training}
                tone="neutral"
              />
              <FilterChip
                active={filter === 'candidate'}
                onClick={() => setFilter('candidate')}
                label="Кандидаты"
                count={statusCounts.candidate}
                tone="warning"
              />
              <FilterChip
                active={filter === 'dismissed'}
                onClick={() => setFilter('dismissed')}
                label="Уволенные"
                count={statusCounts.dismissed}
                tone="neutral"
              />
              <FilterChip
                active={filter === 'attention'}
                onClick={() => setFilter('attention')}
                label="Внимание"
                count={statusCounts.attention}
                tone="error"
              />
            </div>

            <div className="flex w-full sm:w-auto">
              <div className="relative w-full shrink-0 sm:w-[280px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder="Поиск педагога"
                  className="input h-9 w-full pl-9 text-sm"
                />
              </div>
            </div>
          </div>


          {filteredTeachers.length === 0 ? (
            <Card className="border border-border text-sm text-muted-foreground">
              По выбранному фильтру педагогов не найдено.
            </Card>
          ) : (
            <div className="grid gap-4 min-[1100px]:grid-cols-[minmax(0,1fr)_340px]">
              <Card className="overflow-hidden border border-border !p-0 shadow-sm">
                <div className="hidden grid-cols-[minmax(210px,1.45fr)_110px_105px_185px_minmax(145px,0.85fr)_32px] gap-3 border-b border-border bg-muted/40 px-4 py-2 text-xs font-medium text-muted-foreground md:grid">
                  <div>Педагог</div>
                  <div>Статус</div>
                  <div>Качество</div>
                  <div>Последние отсмотры</div>
                  <div>Сейчас</div>
                  <div />
                </div>

                <div className="divide-y divide-border/70">
                  {filteredTeachers.map(teacher => (
                    <button
                      key={teacher.teacherId}
                      type="button"
                      onClick={() => openTeacher(teacher)}
                      className={`w-full px-4 py-3 text-left transition-colors hover:bg-muted/40 ${selectedTeacherId === teacher.teacherId && isWide ? 'bg-primary/5' : ''}`}
                    >
                      <div className="flex items-start gap-3 md:grid md:grid-cols-[minmax(210px,1.45fr)_110px_105px_185px_minmax(145px,0.85fr)_32px] md:items-center md:gap-3">
                        <div className="flex min-w-0 flex-1 items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-sm font-semibold text-foreground">
                            {teacherInitials(teacher.fullName)}
                          </div>
                          <div className="min-w-0">
                            <div className="flex min-w-0 flex-wrap items-center gap-1.5">
                              <div className="truncate font-medium">{teacher.fullName}</div>
                              {teacher.isSeniorTeacher && (
                                <Badge variant="warning" className="px-2 py-0.5 text-[10px] leading-none">Старший педагог</Badge>
                              )}
                            </div>
                            <div className="mt-0.5 truncate text-xs text-muted-foreground">
                              {teacher.qualification ? qualificationLabels[teacher.qualification] : '—'}
                            </div>
                          </div>
                        </div>

                        <div className="hidden md:block">
                          <Badge variant={statusVariant(teacher.status)} className="px-2 py-0.5 text-xs">
                            {statusLabels[teacher.status]}
                          </Badge>
                        </div>
                        <div className="hidden space-y-1 text-xs tabular-nums md:block">
                          <div><span className="text-muted-foreground">УК</span> <span className="font-semibold">{formatPercent(teacher.managementCompanyAverageQualityPercent ?? teacher.managementCompanyLatestReviewQualityPercent)}</span></div>
                          <div><span className="text-muted-foreground">Старший</span> <span className="font-semibold">{formatPercent(teacher.seniorTeacherAverageQualityPercent ?? teacher.seniorTeacherLatestReviewQualityPercent)}</span></div>
                        </div>
                        <div className="hidden space-y-1 md:block">
                          <div className="whitespace-nowrap text-xs text-foreground">{formatReviewSourceLine('УК', teacher.managementCompanyLatestReviewDate, teacher.managementCompanyLatestReviewQualityPercent)}</div>
                          <div className="whitespace-nowrap text-xs text-foreground">{formatReviewSourceLine('Старший', teacher.seniorTeacherLatestReviewDate, teacher.seniorTeacherLatestReviewQualityPercent)}</div>
                        </div>
                        <div className="hidden min-w-0 text-sm text-muted-foreground md:block">
                          <div className="truncate">{teacherNowLine(teacher)}</div>
                        </div>
                        <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground md:mt-0" />
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 pl-[52px] text-xs md:hidden">
                        <Badge variant={statusVariant(teacher.status)} className="px-2 py-0.5 text-xs">
                          {statusLabels[teacher.status]}
                        </Badge>
                        {teacher.managementCompanyReviewsCount > 0 && (
                          <span className="font-medium">УК {formatPercent(teacher.managementCompanyAverageQualityPercent ?? teacher.managementCompanyLatestReviewQualityPercent)}</span>
                        )}
                        {teacher.seniorTeacherReviewsCount > 0 && (
                          <span className="font-medium">Старший {formatPercent(teacher.seniorTeacherAverageQualityPercent ?? teacher.seniorTeacherLatestReviewQualityPercent)}</span>
                        )}
                        <span className="text-muted-foreground">{teacherNowLine(teacher)}</span>
                      </div>
                    </button>
                  ))}
                </div>

                {filteredTeachers.length === 0 && (
                  <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                    По выбранному фильтру педагогов не найдено.
                  </div>
                )}
              </Card>

              <div className="hidden min-[1100px]:block">
                {selectedTeacher ? (
                  <TeacherPreview
                    teacher={selectedTeacher}
                    onOpen={() => navigate(`/partners/${partnerNumber}/studios/${studioId}/teachers/${selectedTeacher.teacherId}`)}
                  />
                ) : (
                  <Card className="border border-border text-sm text-muted-foreground">
                    Выберите педагога, чтобы посмотреть краткую информацию.
                  </Card>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  tone,
}: {
  active: boolean
  onClick: () => void
  label: string
  count: number
  tone: 'neutral' | 'success' | 'warning' | 'error'
}) {
  const idleClass = tone === 'success'
    ? 'border-success/20 bg-success/5 text-success hover:bg-success/10'
    : tone === 'warning'
      ? 'border-primary/30 bg-primary/5 text-warning hover:bg-primary/10'
      : tone === 'error'
        ? 'border-error/20 bg-error/5 text-error hover:bg-error/10'
        : 'border-border bg-card text-muted-foreground hover:bg-muted'

  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-9 shrink-0 rounded-full border px-3 text-sm font-medium whitespace-nowrap transition-colors ${
        active ? 'border-primary bg-primary text-primary-foreground' : idleClass
      }`}
    >
      {label}: <span className="tabular-nums">{count}</span>
    </button>
  )
}


function TeacherPreview({ teacher, onOpen }: { teacher: PartnerTeacherListItem; onOpen: () => void }) {
  const trainingLine = trainingStatusLine(teacher)
  return (
    <Card className="sticky top-[calc(var(--header-height,64px)+12px)] border border-border shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/15 text-base font-semibold">
          {teacherInitials(teacher.fullName)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="font-semibold">{teacher.fullName}</div>
            {teacher.isSeniorTeacher && (
              <Badge variant="warning" className="px-2 py-0.5 text-[10px] leading-none">Старший педагог</Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {teacher.qualification ? qualificationLabels[teacher.qualification] : 'Направление не указано'}
          </div>
          <Badge variant={statusVariant(teacher.status)} className="mt-2 px-2 py-0.5 text-xs">
            {statusLabels[teacher.status]}
          </Badge>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2">
        <SourceMiniMetric
          label="УК"
          count={teacher.managementCompanyReviewsCount}
          quality={teacher.managementCompanyAverageQualityPercent ?? teacher.managementCompanyLatestReviewQualityPercent}
          lastDate={teacher.managementCompanyLatestReviewDate}
        />
        <SourceMiniMetric
          label="Старший педагог"
          count={teacher.seniorTeacherReviewsCount}
          quality={teacher.seniorTeacherAverageQualityPercent ?? teacher.seniorTeacherLatestReviewQualityPercent}
          lastDate={teacher.seniorTeacherLatestReviewDate}
        />
      </div>

      <div className="mt-4 border-t border-border pt-4">
        <div className="text-sm font-semibold">Сейчас</div>
        <div className="mt-3 space-y-2.5 text-sm">
          <PreviewLine icon={BookOpen} text={trainingLine} />
          {teacher.managementCompanyLatestReviewDate && (
            <PreviewLine
              icon={CalendarDays}
              text={`УК · ${formatPercent(teacher.managementCompanyLatestReviewQualityPercent)} · ${formatDate(teacher.managementCompanyLatestReviewDate)}`}
            />
          )}
          {teacher.seniorTeacherLatestReviewDate && (
            <PreviewLine
              icon={CalendarDays}
              text={`Старший педагог · ${formatPercent(teacher.seniorTeacherLatestReviewQualityPercent)} · ${formatDate(teacher.seniorTeacherLatestReviewDate)}`}
            />
          )}
        </div>
      </div>

      <Button className="mt-4 w-full" onClick={onOpen}>
        Открыть карточку
      </Button>
    </Card>
  )
}

function SourceMiniMetric({
  label,
  count,
  quality,
  lastDate,
}: {
  label: string
  count: number
  quality: number | null
  lastDate: string | null
}) {
  return (
    <div className="rounded-lg border border-border bg-muted/30 px-2.5 py-2.5">
      <div className="text-[11px] font-medium text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm font-semibold tabular-nums">{formatPercent(quality)}</div>
      <div className="mt-0.5 text-[11px] text-muted-foreground">
        {count > 0 ? `Отсмотров: ${count} · ${formatDate(lastDate)}` : 'Отсмотров: 0'}
      </div>
    </div>
  )
}

function PreviewLine({ icon: Icon, text }: { icon: typeof Users; text: string }) {
  return (
    <div className="flex items-start gap-2 text-muted-foreground">
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{text}</span>
    </div>
  )
}
